import { responsiblePlhIdOfClient } from "./client-plh-lookup.js";
import type { RequestContext } from "../context/request-context.js";
import type { NdtMethodReportRepository } from "../../repositories/operational-repositories.js";
import {
  NDT_RESPONSIBILITY_TERM,
  NDT_RESULT_LEGEND,
  NDT_RESULT_LEGEND_NOTES,
  NDT_SIGNATORY_ROLE_LABELS,
  isEmbeddableImage,
  type NdtDocumentCommon,
  type NdtHistoryEntry,
  type NdtReportCommon,
  type NdtSignatory,
  type NdtValidationResult
} from "../../domain/ndt/ndt-report-common.js";
import { formatCivilDateBR } from "../../domain/ndt/lp-consumable.js";
import { canActAsPlh } from "../../domain/identity/plh-eligibility.js";
import type { ClientId, EquipmentId } from "../../shared/kernel/identifiers.js";
import type { DocumentNumberAllocator } from "./document-numbering-service.js";
import type { NumberedDocumentType } from "../../domain/settings/document-numbering.js";
import type { DocumentVisual, TemplatedDocument } from "../../domain/studio/visual-design.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import type { DocumentReviewHooks } from "./technical-review-service.js";
import type { DocumentHoldHooks } from "./document-hold-service.js";
import type { TechnicalDocumentType } from "../../domain/review/technical-document-review.js";

const randomUUID = () => globalThis.crypto.randomUUID();

const DEFAULT_SIGNATORY_TITLES: Record<NdtSignatory["role"], string> = {
  INSPETOR: "Inspetor de END",
  PLH: "Engenheiro – PLH",
  FISCAL: "Fiscal"
};

export interface NdtReportSeed {
  clientId?: string | null;
  clientDisplayName?: string;
  projectId?: string | null;
  projectContract?: string;
  unitAreaId?: string | null;
  unitArea?: string;
  equipmentId?: string | null;
  equipmentTag?: string;
  equipmentTypeLabel?: string;
  component?: string;
  baseMaterial?: string;
  inspectionId?: string | null;
  inspectionNumber?: string;
  inspectorProfessionalId?: string | null;
  inspectorName?: string;
}

export type NdtInspectionLookup = (inspectionId: string) => Promise<{ id: string; equipmentId: string; clientId: string } | null>;

export interface NdtReportServiceConfig {
  /** "PM", "USC" — tipo do DocumentEngine e prefixo do número. */
  documentType: string;
  numberPrefix: string;
  /** "Relatório PM" — para mensagens. */
  reportLabel: string;
  permissions: { view: string; create: string; edit: string; validate: string; approve: string; sign: string; manageSignatories: string };
}

/** Os Profissionais por papel mudaram entre o gravado e o enviado? */
export function ndtSignatoriesDiffer(existing: NdtReportCommon<unknown>, incoming: NdtReportCommon<unknown>): boolean {
  const key = (r: NdtReportCommon<unknown>) =>
    (["INSPETOR", "PLH", "FISCAL"] as const)
      .map(role => `${role}=${r.signatories?.find(s => s.role === role)?.professionalId ?? ""}`)
      .join("|") + `|inspector=${r.inspectorProfessionalId ?? ""}`;
  return key(existing) !== key(incoming);
}

/**
 * Comportamento comum dos relatórios de END por método (o mesmo desenho do
 * LP): rascunho com número gerado uma vez, salvar preservando identidade e
 * trilha, validar, modelo documental único para Preview e PDF, assinatura
 * PLH pelo gate real com congelamento do documento. Cada método traz o seu
 * domínio e o seu `composeModel`.
 */
export abstract class NdtReportServiceBase<R extends NdtReportCommon<M>, M extends NdtDocumentCommon> {
  /** Revisão PLH central: bloqueia edição enquanto o documento está com o PLH e condiciona a assinatura à aprovação. */
  private reviewHooks: DocumentReviewHooks | null = null;

  attachReviewHooks(hooks: DocumentReviewHooks): void {
    this.reviewHooks = hooks;
  }

  /** HOLD: enquanto o relatório está EM HOLD nada é salvo/validado/assinado — o status do módulo não muda. */
  private holdHooks: DocumentHoldHooks | null = null;

  attachHoldHooks(hooks: DocumentHoldHooks): void {
    this.holdHooks = hooks;
  }

  private async assertNotLockedByReview(id: string): Promise<void> {
    if (this.holdHooks) {
      const hold = await this.holdHooks.state(this.config.documentType as TechnicalDocumentType, id);
      if (hold.onHold) throw new AppError(ErrorCode.Conflict, "Report on hold", { safeMessage: hold.reason });
    }
    if (!this.reviewHooks) return;
    const state = await this.reviewHooks.state(this.config.documentType as TechnicalDocumentType, id);
    if (state.locked) throw new AppError(ErrorCode.Conflict, "Report locked by PLH review", { safeMessage: state.reason });
  }

  protected constructor(
    protected readonly repo: NdtMethodReportRepository<R>,
    protected readonly config: NdtReportServiceConfig,
    protected readonly identityRepo?: any,
    protected readonly inspectionLookup?: NdtInspectionLookup
  ) {}

  /** Verificação de instrumentos da M21 injetada pelo composition root, nunca pela UI. */
  private ndtAssetValidator: ((context: RequestContext, report: R) => Promise<string[]>) | null = null;

  attachNdtAssetValidator(validator: (context: RequestContext, report: R) => Promise<string[]>): void {
    this.ndtAssetValidator = validator;
  }

  protected abstract newReport(base: NdtReportCommon<M>, seed: NdtReportSeed | undefined): R;
  protected abstract normalize(report: R): R;
  protected abstract validateCompleteness(report: R): NdtValidationResult;
  protected abstract composeSpecific(report: R, common: NdtDocumentCommon): Promise<M>;

  private get auth() { return new RbacAuthorizationService(); }

  private async assertInspectionMatchesContext(inspectionId: string | null | undefined, equipmentId: string | null | undefined, clientId: string): Promise<void> {
    if (!inspectionId || !this.inspectionLookup) return;
    const inspection = await this.inspectionLookup(inspectionId);
    if (!inspection) {
      throw new AppError(ErrorCode.NotFound, `Inspection ${inspectionId} not found`, { safeMessage: "A Inspeção selecionada não foi encontrada." });
    }
    if (inspection.clientId && inspection.clientId !== clientId) {
      throw new AppError(ErrorCode.Validation, "Inspection belongs to another client", { safeMessage: "A Inspeção selecionada pertence a outro Cliente." });
    }
    if (equipmentId && inspection.equipmentId && inspection.equipmentId !== equipmentId) {
      throw new AppError(ErrorCode.Validation, "Inspection belongs to another equipment", { safeMessage: "A Inspeção selecionada pertence a outro Equipamento — escolha uma Inspeção deste Equipamento ou deixe o campo vazio." });
    }
  }

  async getReport(context: RequestContext, id: string): Promise<R | null> {
    this.auth.require(context, this.config.permissions.view as any);
    const report = await this.repo.findById(context, id);
    return report ? this.normalize(report) : null;
  }

  async listByClient(context: RequestContext, clientId?: string): Promise<readonly R[]> {
    this.auth.require(context, this.config.permissions.view as any);
    const target = clientId ?? context.activeClientId;
    if (!target) return [];
    return (await this.repo.listByClient(context, String(target))).map(r => this.normalize(r));
  }

  async getByInspection(context: RequestContext, inspectionId: string): Promise<R | null> {
    this.auth.require(context, this.config.permissions.view as any);
    const report = await this.repo.findByInspection(context, inspectionId);
    return report ? this.normalize(report) : null;
  }

  async createDraft(context: RequestContext, seed?: NdtReportSeed): Promise<R> {
    this.auth.require(context, this.config.permissions.create as any);
    const clientId = (seed?.clientId || context.activeClientId) as ClientId;
    if (!clientId) {
      throw new AppError(ErrorCode.Validation, "No client selected", { safeMessage: `Selecione o Cliente do relatório antes de criar o ${this.config.reportLabel}.` });
    }
    if (seed?.inspectionId) {
      await this.assertInspectionMatchesContext(seed.inspectionId, seed.equipmentId ?? null, clientId);
      const existing = await this.repo.findByInspection(context, seed.inspectionId);
      if (existing) {
        throw new AppError(ErrorCode.Conflict, "Report already exists for this inspection", { safeMessage: `Já existe um ${this.config.reportLabel} para esta Inspeção (Nº ${existing.reportNumber}). Abra o relatório existente em vez de criar outro.` });
      }
    }
    const now = new Date().toISOString();
    const reportNumber = await this.generateReportNumber(context, clientId, { projectId: seed?.projectId ?? null, unitAreaId: seed?.unitAreaId ?? null });
    const by = context.userId || "system";
    const base: NdtReportCommon<M> = {
      id: `${this.config.documentType.toLowerCase()}-${randomUUID()}`,
      clientId,
      reportNumber,
      clientReportNumber: "",
      revision: "00",
      status: "DRAFT",
      projectId: seed?.projectId ?? null,
      unitAreaId: seed?.unitAreaId ?? null,
      equipmentId: (seed?.equipmentId as EquipmentId) || null,
      inspectionId: (seed?.inspectionId as any) || null,
      clientDisplayName: seed?.clientDisplayName || "",
      projectContract: seed?.projectContract || "",
      unitArea: seed?.unitArea || "",
      equipmentTag: seed?.equipmentTag || "",
      equipmentTypeLabel: seed?.equipmentTypeLabel || "",
      component: seed?.component || "",
      inspectionNumber: seed?.inspectionNumber || "",
      osWorkOrderId: null,
      osNumber: "",
      osSubWorkOrderId: null,
      subOsNumber: "",
      testDate: "",
      inspectorProfessionalId: (seed?.inspectorProfessionalId as any) ?? context.professionalId ?? null,
      inspectorName: seed?.inspectorName || "",
      procedureId: null,
      procedureRevision: "",
      referenceNormId: null,
      referenceNorm: "",
      acceptanceCriteriaId: null,
      acceptanceCriteria: "",
      baseMaterial: seed?.baseMaterial || "",
      testTemperature: "",
      croqui: null,
      photos: [],
      observations: "",
      signatories: await this.prefillInspectorSignatory(seed?.inspectorProfessionalId ?? null),
      issueDate: now,
      history: [{ at: now, by, action: "CREATED", detail: `Rascunho ${reportNumber} criado.` }],
      frozenSnapshot: null,
      signedAt: null,
      signedByUserId: null,
      signedByProfessionalId: null,
      createdAt: now,
      updatedAt: now,
      createdBy: by
    };
    const report = this.newReport(base, seed);
    await this.repo.save(context, report);
    return report;
  }

  async saveDraft(context: RequestContext, report: R): Promise<R> {
    this.auth.require(context, this.config.permissions.edit as any);
    const existing = await this.requireReport(context, report.id);
    if (existing.status === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "Signed report cannot be edited", { safeMessage: "Relatório assinado não pode mais ser editado." });
    }

    // Inspetor só pode salvar relatórios em que seja o inspetor responsável
    if (!context.permissions.has(this.config.permissions.manageSignatories as any) && context.professionalId) {
      if (existing.inspectorProfessionalId && String(existing.inspectorProfessionalId) !== String(context.professionalId)) {
        throw new AppError(ErrorCode.Authorization, "Você só possui permissão para editar relatórios sob sua responsabilidade técnica.");
      }
      if (report.inspectorProfessionalId && String(report.inspectorProfessionalId) !== String(context.professionalId)) {
        throw new AppError(ErrorCode.Authorization, "Você só possui permissão para editar relatórios sob sua responsabilidade técnica.");
      }
    }

    await this.assertNotLockedByReview(report.id);
    if (ndtSignatoriesDiffer(existing, report) && !context.permissions.has(this.config.permissions.manageSignatories as any)) {
      throw new AppError(ErrorCode.Authorization, "Changing signatories requires engineering permission", { safeMessage: `Somente o Engenheiro autorizado pode alterar os signatários do ${this.config.reportLabel}.` });
    }
    if (report.inspectionId && report.inspectionId !== existing.inspectionId) {
      await this.assertInspectionMatchesContext(report.inspectionId, report.equipmentId ?? existing.equipmentId, existing.clientId);
    }
    const now = new Date().toISOString();
    const updated: R = {
      ...this.normalize({ ...report }),
      id: existing.id,
      clientId: existing.clientId,
      reportNumber: existing.reportNumber,
      revision: existing.revision,
      createdAt: existing.createdAt,
      createdBy: existing.createdBy,
      history: this.appendHistory(existing.history, { at: now, by: context.userId || "system", action: "SAVED", detail: "Rascunho salvo." }),
      frozenSnapshot: existing.frozenSnapshot,
      status: existing.status === "VALIDATED" ? "DRAFT" : existing.status,
      issueDate: now,
      updatedAt: now
    };
    await this.repo.save(context, updated);
    if (this.reviewHooks) await this.reviewHooks.correctionSaved(context, this.config.documentType as TechnicalDocumentType, updated.id);
    return updated;
  }

  private async assertNotOnHold(id: string): Promise<void> {
    if (!this.holdHooks) return;
    const hold = await this.holdHooks.state(this.config.documentType as TechnicalDocumentType, id);
    if (hold.onHold) throw new AppError(ErrorCode.Conflict, "Report on hold", { safeMessage: hold.reason });
  }

  /**
   * CONFIGURAÇÕES → END: obrigatoriedades ACRESCENTADAS ao mínimo do domínio
   * (foto, croqui). Valem na VALIDAÇÃO — que é o que libera o relatório ao
   * PLH —, não na assinatura: um documento já aprovado não pode passar a ser
   * recusado porque a regra mudou depois da aprovação.
   */
  private endRequirements: ((documentType: string) => Promise<{ requirePhoto: boolean; requireCroqui: boolean } | null>) | null = null;

  attachEndRequirements(provider: (documentType: string) => Promise<{ requirePhoto: boolean; requireCroqui: boolean } | null>): void {
    this.endRequirements = provider;
  }

  protected async completenessWithSettings(context: RequestContext, report: R): Promise<NdtValidationResult> {
    const base = this.validateCompleteness(report);
    const required = this.endRequirements ? await this.endRequirements(this.config.documentType).catch(() => null) : null;
    const errors = [...base.errors];
    if (this.ndtAssetValidator) errors.push(...await this.ndtAssetValidator(context, report));
    if (!required) return { valid: errors.length === 0, errors };
    if (required.requirePhoto && (!Array.isArray(report.photos) || report.photos.length === 0)) errors.push("Anexe ao menos uma fotografia (exigência definida em Configurações → END).");
    if (required.requireCroqui && !report.croqui) errors.push("Anexe o croqui (exigência definida em Configurações → END).");
    return { valid: errors.length === 0, errors };
  }

  async validate(context: RequestContext, id: string): Promise<NdtValidationResult> {
    this.auth.require(context, this.config.permissions.validate as any);
    await this.assertNotOnHold(id);
    const report = await this.requireReport(context, id);
    const result = await this.completenessWithSettings(context, report);
    if (report.status !== "SIGNED") {
      const now = new Date().toISOString();
      await this.repo.save(context, {
        ...report,
        status: result.valid ? "VALIDATED" : "DRAFT",
        history: this.appendHistory(report.history, { at: now, by: context.userId || "system", action: "VALIDATED", detail: result.valid ? "Dados validados — relatório completo." : `Validação apontou ${result.errors.length} pendência(s).` }),
        updatedAt: now
      });
    }
    return result;
  }

  /** Depois de assinado devolve o snapshot congelado, não os cadastros vivos. */
  async buildDocumentModel(context: RequestContext, id: string): Promise<M> {
    this.auth.require(context, this.config.permissions.view as any);
    const report = await this.requireReport(context, id);
    if (report.status === "SIGNED" && report.frozenSnapshot) return report.frozenSnapshot;
    return this.composeModel(report);
  }

  async buildDocumentSnapshot(context: RequestContext, id: string): Promise<DocumentSnapshot> {
    const model = await this.buildDocumentModel(context, id);
    const report = (await this.repo.findById(context, id))!;
    return { id: report.reportNumber, type: this.config.documentType, revision: report.revision, documentNumber: report.reportNumber, clientId: report.clientId, createdAt: new Date(report.issueDate), data: model };
  }

  async recordPdfGenerated(context: RequestContext, id: string): Promise<void> {
    const report = await this.repo.findById(context, id);
    if (!report) return;
    const now = new Date().toISOString();
    await this.repo.save(context, { ...report, history: this.appendHistory(report.history, { at: now, by: context.userId || "system", action: "PDF_GENERATED", detail: `PDF gerado (Rev. ${report.revision}).` }) });
  }

  /** Assinatura PLH: permissões + gate real; congela tudo (cadastros, figuras, imagens, assinaturas). */
  async signAsPlh(context: RequestContext, id: string): Promise<R> {
    this.auth.require(context, this.config.permissions.approve as any);
    this.auth.require(context, this.config.permissions.sign as any);
    const report = await this.requireReport(context, id);
    if (report.status === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "Report already signed", { safeMessage: "Relatório já assinado." });
    }
    await this.assertNotOnHold(id);
    if (this.reviewHooks) {
      const state = await this.reviewHooks.state(this.config.documentType as TechnicalDocumentType, id);
      if (state.review && !state.approved) {
        throw new AppError(ErrorCode.Conflict, "Review not approved", { safeMessage: "O relatório só pode ser assinado depois de aprovado pelo PLH na revisão." });
      }
    }
    const completeness = this.validateCompleteness(report);
    if (this.ndtAssetValidator) {
      completeness.errors.push(...await this.ndtAssetValidator(context, report));
      completeness.valid = completeness.errors.length === 0;
    }
    if (!completeness.valid) {
      throw new AppError(ErrorCode.Validation, "Report incomplete", { safeMessage: `O relatório não pode ser assinado com pendências: ${completeness.errors.join(" ")}` });
    }
    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId) : null;
    // Habilitação pelo CLIENTE DO DOCUMENTO: quem o Cliente nomeou responsável.
    const eligibility = canActAsPlh(user, professional, await responsiblePlhIdOfClient(this.identityRepo as never, String(report.clientId)));
    if (!eligibility.allowed) {
      throw new AppError(ErrorCode.Authorization, eligibility.reason || "Not a PLH", { safeMessage: eligibility.reason || "Você não está habilitado a assinar como PLH." });
    }
    const chosenPlh = report.signatories.find(s => s.role === "PLH" && s.professionalId);
    if (chosenPlh && chosenPlh.professionalId !== professional.id) {
      throw new AppError(ErrorCode.Authorization, "Signer is not the selected PLH", { safeMessage: `O PLH selecionado para este relatório é ${chosenPlh.name || chosenPlh.professionalId}. Somente ele pode assinar como PLH.` });
    }
    const now = new Date().toISOString();
    const signatories = report.signatories.filter(s => s.role !== "PLH");
    signatories.push({ role: "PLH", professionalId: professional.id, name: professional.name || "", functionTitle: professional.functionTitle || DEFAULT_SIGNATORY_TITLES.PLH, credential: this.credentialFor(professional), signatureImageId: professional.signatureImageId || null, signedAt: now });
    const signed: R = { ...report, signatories, status: "SIGNED", signedAt: now, signedByUserId: context.userId || null, signedByProfessionalId: professional.id, issueDate: now, updatedAt: now };
    signed.frozenSnapshot = await this.composeModel(signed);
    signed.history = this.appendHistory(report.history, { at: now, by: context.userId || "system", action: "SIGNED", detail: `Assinado como PLH por ${professional.name}. Documento congelado.` });
    await this.repo.save(context, signed);
    if (this.reviewHooks) await this.reviewHooks.signed(context, this.config.documentType as TechnicalDocumentType, signed.id);
    return signed;
  }

  // ---------------------------------------------------------------------

  /**
   * STUDIO INSPECT IA: o visual PUBLICADO entra no modelo documental quando
   * ele é montado. Aprovado/congelado = modelo congelado = visual da época.
   * Sem a ligação (testes, composições antigas), o modelo sai sem visual e o
   * builder imprime o padrão vigente — exatamente como antes.
   */
  private documentVisual: ((type: TemplatedDocument) => Promise<DocumentVisual>) | null = null;

  attachDocumentVisual(provider: (type: TemplatedDocument) => Promise<DocumentVisual>): void {
    this.documentVisual = provider;
  }

  protected async composeModel(report: R): Promise<M> {
    const photos = report.photos
      .filter(p => isEmbeddableImage(p.image))
      .map((p, index) => ({ number: `Foto ${String(index + 1).padStart(2, "0")}`, image: p.image, item: p.item, location: p.location, comment: p.comment }));
    const common: NdtDocumentCommon = {
      visual: this.documentVisual ? await this.documentVisual(this.config.documentType as TemplatedDocument).catch(() => null) : null,
      reportNumber: report.reportNumber,
      clientReportNumber: report.clientReportNumber,
      revision: report.revision,
      status: report.status,
      issueDate: formatCivilDateBR(report.issueDate.slice(0, 10)),
      osNumber: report.osNumber,
      subOsNumber: report.subOsNumber,
      qrPayload: `INSPECT-IA|${this.config.documentType}|${report.reportNumber}|REV ${report.revision}|${report.id}`,
      identification: {
        client: report.clientDisplayName,
        projectContract: report.projectContract,
        unitArea: report.unitArea,
        equipmentTag: report.equipmentTag,
        equipmentType: report.equipmentTypeLabel,
        osSubOs: [report.osNumber, report.subOsNumber].filter(Boolean).join(" / "),
        testDate: formatCivilDateBR(report.testDate),
        inspector: report.inspectorName,
        inspectionNumber: report.inspectionId ? report.inspectionNumber : ""
      },
      croqui: report.croqui && isEmbeddableImage(report.croqui.image) ? { image: report.croqui.image, fileName: report.croqui.fileName } : null,
      photos,
      observations: report.observations,
      legend: NDT_RESULT_LEGEND,
      legendNotes: [...NDT_RESULT_LEGEND_NOTES],
      responsibilityTerm: NDT_RESPONSIBILITY_TERM,
      signatories: await this.resolveSignatories(report)
    };
    return this.composeSpecific(report, common);
  }

  private async resolveSignatories(report: R): Promise<NdtDocumentCommon["signatories"]> {
    const roles: NdtSignatory["role"][] = ["INSPETOR", "PLH", "FISCAL"];
    const byRole = new Map(report.signatories.map(s => [s.role, s]));
    return Promise.all(roles.map(async role => {
      const s = byRole.get(role);
      let signatureImage: string | null = null;
      if (s?.professionalId && this.identityRepo?.getProfessionalSignature) {
        try { signatureImage = await this.identityRepo.getProfessionalSignature(s.professionalId); } catch { signatureImage = null; }
      }
      const date = s?.signedAt || (s?.professionalId ? report.issueDate : "");
      return {
        role,
        roleLabel: NDT_SIGNATORY_ROLE_LABELS[role],
        name: s?.name || "",
        functionTitle: s?.functionTitle || (s ? DEFAULT_SIGNATORY_TITLES[role] : ""),
        credential: s?.credential || "",
        signatureImage: isEmbeddableImage(signatureImage) ? signatureImage : null,
        date: date ? formatCivilDateBR(date.slice(0, 10)) : ""
      };
    }));
  }

  protected credentialFor(p: any): string {
    return p.crea || p.cft || p.snqc || p.sncc || p.endIso9712 || p.endIso || "";
  }

  private async prefillInspectorSignatory(professionalId: string | null): Promise<NdtSignatory[]> {
    if (!professionalId || !this.identityRepo?.getProfessional) return [];
    let professional: any = null;
    try { professional = await this.identityRepo.getProfessional(professionalId); } catch { professional = null; }
    if (!professional) return [];
    return [{ role: "INSPETOR", professionalId: professional.id, name: professional.name || "", functionTitle: professional.functionTitle || DEFAULT_SIGNATORY_TITLES.INSPETOR, credential: this.credentialFor(professional), signatureImageId: professional.signatureImageId || null, signedAt: null }];
  }

  private appendHistory(history: NdtHistoryEntry[] | undefined, entry: NdtHistoryEntry): NdtHistoryEntry[] {
    return [...(Array.isArray(history) ? history : []), entry];
  }

  protected async requireReport(context: RequestContext, id: string): Promise<R> {
    const report = await this.repo.findById(context, id);
    if (!report) throw new AppError(ErrorCode.NotFound, "Report not found", { safeMessage: `${this.config.reportLabel} não encontrado.` });
    return this.normalize(report);
  }

  /**
   * CONFIGURAÇÕES → NUMERAÇÃO. Com o alocador ligado, o número vem do modelo
   * configurado e de uma sequência ATÔMICA; sem ele (testes, composições que
   * não o fornecem), vale o gerador histórico.
   */
  private numbering: DocumentNumberAllocator | null = null;

  attachNumberAllocator(allocator: DocumentNumberAllocator): void {
    this.numbering = allocator;
  }

  private async generateReportNumber(context: RequestContext, clientId: string, refs: { projectId?: string | null; unitAreaId?: string | null } = {}): Promise<string> {
    const all = await this.repo.listByClient(context, clientId);
    const year = new Date().getFullYear();
    const prefix = `${this.config.numberPrefix}-${year}-`;
    const count = all.filter(r => r.reportNumber.startsWith(prefix)).length;
    if (this.numbering) {
      return this.numbering.allocate({
        documentType: this.config.documentType as NumberedDocumentType,
        clientId, projectId: refs.projectId ?? null, unitAreaId: refs.unitAreaId ?? null,
        legacyCount: async () => count,
        isTaken: async (candidate, scope) => {
          const pool = scope.perClient || !this.repo.listAll ? await this.repo.listByClient(context, clientId) : await this.repo.listAll();
          return pool.some(r => r.reportNumber === candidate);
        }
      });
    }
    return `${prefix}${String(count + 1).padStart(4, "0")}`;
  }
}
